import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { CloseOutlined, SaveOutlined, SearchOutlined } from "@mui/icons-material";
import { Alert, Checkbox, CircularProgress, Dialog, FormControlLabel, IconButton, Switch, TextField, Tooltip } from "@mui/material";

import { useCreateRoleMutation } from "state/rtkQuery/access/accessMutations";
import { extractApiErrorMessage } from "utils/accessManagement/extractApiErrorMessage";
import { buildPermissionTree, getWildcardImpliedPermissions, groupPermissionsByNamespace } from "utils/accessManagement/roleDisplay";
import { showToast } from "utils/reusableToast";

import "./RoleEditModal.css";

const EMPTY_FORM = { roleName: "", description: "", priority: 50, isActive: true };

// Right-side drawer for POST /roles — same tree-editor UI as RoleEditDrawer
// (namespace tabs, wildcard-implied locking, search) but starting from an
// empty permission set and the full backend catalog rather than a union with
// an existing role's own permissions.
const CreateRoleDrawer = ({ open, onClose, knownPermissionCatalog, onCreated }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [permissions, setPermissions] = useState(new Set());
  const [search, setSearch] = useState("");
  const [activeNamespace, setActiveNamespace] = useState(null);
  const [errors, setErrors] = useState({});
  const [createRole, { isLoading, isError, error, reset }] = useCreateRoleMutation();

  useEffect(() => {
    if (open) {
      setForm(EMPTY_FORM);
      setPermissions(new Set());
      setSearch("");
      setActiveNamespace(null);
      setErrors({});
      reset();
    }
  }, [open]); // eslint-disable-line

  const catalog = useMemo(() => [...(knownPermissionCatalog || [])].sort(), [knownPermissionCatalog]);
  const groups = useMemo(() => groupPermissionsByNamespace(catalog), [catalog]);

  const impliedByGroup = useMemo(() => {
    const map = new Map();
    groups.forEach((group) => {
      map.set(group.namespace, getWildcardImpliedPermissions(permissions, group.permissions));
    });
    return map;
  }, [groups, permissions]);

  const effectivePermissions = useMemo(() => {
    const all = new Set(permissions);
    impliedByGroup.forEach((implied) => implied.forEach((p) => all.add(p)));
    return all;
  }, [permissions, impliedByGroup]);

  const resolvedActiveNamespace =
    groups.find((g) => g.namespace === activeNamespace)?.namespace ?? groups[0]?.namespace ?? null;
  const activeGroupUnfiltered = groups.find((g) => g.namespace === resolvedActiveNamespace) ?? null;
  const activeGroup = useMemo(() => {
    if (!activeGroupUnfiltered) return null;
    if (!search.trim()) return activeGroupUnfiltered;
    const q = search.toLowerCase();
    return { ...activeGroupUnfiltered, permissions: activeGroupUnfiltered.permissions.filter((p) => p.toLowerCase().includes(q)) };
  }, [activeGroupUnfiltered, search]);

  const activeGroupTree = useMemo(
    () => (activeGroup ? buildPermissionTree(activeGroup.permissions) : { parents: [], leaves: [] }),
    [activeGroup]
  );

  const isImplied = (permission, namespace) => impliedByGroup.get(namespace)?.has(permission) ?? false;

  const togglePermission = (permission, namespace) => {
    if (isImplied(permission, namespace)) return;
    setPermissions((prev) => {
      const next = new Set(prev);
      if (next.has(permission)) next.delete(permission);
      else next.add(permission);
      return next;
    });
  };

  const toggleGroup = (group, checkAll) => {
    setPermissions((prev) => {
      const next = new Set(prev);
      group.permissions.forEach((p) => (checkAll ? next.add(p) : next.delete(p)));
      return next;
    });
  };

  const activeGroupAllSelected = !!activeGroup && activeGroup.permissions.every((p) => effectivePermissions.has(p));
  const hasParents = activeGroupTree.parents.length > 0;

  const validate = () => {
    const nextErrors = {};
    if (!form.roleName.trim()) nextErrors.roleName = "Role name is required.";
    if (form.priority === "" || Number.isNaN(Number(form.priority))) nextErrors.priority = "Priority must be a number.";
    return nextErrors;
  };

  const handleSubmit = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      const result = await createRole({
        role_name: form.roleName.trim(),
        description: form.description.trim() || undefined,
        priority: Number(form.priority),
        permissions: Array.from(effectivePermissions),
        is_active: form.isActive,
      }).unwrap();

      if (result.not_updated?.length > 0) {
        showToast({
          title: "Role creation failed",
          description: result.not_updated[0]?.message || "The role could not be created.",
          type: "error",
        });
        return;
      }

      showToast({ title: "Role created", description: `"${form.roleName}" was created successfully.`, type: "success" });
      onCreated();
    } catch (err) {
      const fieldErrors = err?.data?.errors;
      if (fieldErrors && typeof fieldErrors === "object") {
        setErrors((e) => ({ ...e, ...fieldErrors }));
      }
    }
  };

  const renderCheckbox = (permission, namespace, { isParent = false } = {}) => {
    const implied = isImplied(permission, namespace);
    const checked = effectivePermissions.has(permission);
    return (
      <Tooltip
        key={permission}
        title={implied ? "Included because a parent scope above is selected" : ""}
        disableHoverListener={!implied}
      >
        <FormControlLabel
          className={`role-modal__checkbox-row${isParent ? " role-modal__checkbox-row--parent" : " role-modal__checkbox-row--child"}`}
          control={<Checkbox size="small" checked={checked} disabled={implied} onChange={() => togglePermission(permission, namespace)} />}
          label={isParent ? `${permission} — full access` : permission}
          sx={implied ? { opacity: 0.6 } : undefined}
        />
      </Tooltip>
    );
  };

  const renderTreeNode = (node, namespace, depth = 0) => (
    <div key={node.permission} className="role-modal__scope-tree" style={{ marginLeft: depth > 0 ? 26 : 0 }}>
      {renderCheckbox(node.permission, namespace, { isParent: true })}
      <div className="role-modal__scope-children">
        {node.children.map((child) => renderTreeNode(child, namespace, depth + 1))}
        {node.leaves.map((permission) => renderCheckbox(permission, namespace))}
      </div>
    </div>
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth={false} PaperProps={{ sx: { width: "min(1200px, 94vw)", height: "min(820px, 90vh)" } }}>
      <div className="role-modal">
        <div className="role-modal__header">
          <div>
            <p className="role-modal__eyebrow">Access Management</p>
            <h2 className="role-modal__title">Create role</h2>
          </div>
          <Tooltip title="Close">
            <IconButton size="small" onClick={onClose} sx={{ color: "#9ca3af" }}>
              <CloseOutlined />
            </IconButton>
          </Tooltip>
        </div>

        <div className="role-modal__body">
          <div className="role-modal__fields">
            <TextField
              label="Role name"
              required
              size="small"
              fullWidth
              value={form.roleName}
              error={!!errors.roleName}
              helperText={errors.roleName}
              onChange={(e) => setForm((f) => ({ ...f, roleName: e.target.value }))}
            />
            <TextField
              label="Description"
              size="small"
              fullWidth
              multiline
              minRows={3}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
            <TextField
              label="Priority"
              type="number"
              size="small"
              fullWidth
              value={form.priority}
              error={!!errors.priority}
              helperText={errors.priority || "Lower number = more senior."}
              onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
            />
            <FormControlLabel
              control={<Switch checked={form.isActive} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))} size="small" />}
              label={form.isActive ? "Active" : "Inactive"}
            />

            <div className="role-modal__summary">
              <span className="role-modal__summary-count">{effectivePermissions.size}</span>
              <span className="role-modal__summary-label">permissions selected</span>
            </div>

            {isError && (
              <Alert severity="error" sx={{ fontSize: "12px" }}>
                {extractApiErrorMessage(error, "Failed to create role. Please try again.")}
              </Alert>
            )}
          </div>

          <div className="role-modal__permissions">
            {groups.length === 0 ? (
              <p style={{ fontSize: 13, color: "#9ca3af" }}>No permissions are available to assign.</p>
            ) : (
              <div className="role-modal__tabbed">
                <div className="role-modal__tabs">
                  <div className="role-modal__tabs-header">
                    <span className="role-modal__tabs-title">Service type</span>
                  </div>
                  {groups.map((group) => {
                    const selectedInGroup = group.permissions.filter((p) => effectivePermissions.has(p)).length;
                    const isActive = resolvedActiveNamespace === group.namespace;
                    return (
                      <button
                        key={group.namespace}
                        type="button"
                        className={`role-modal__tab${isActive ? " active" : ""}`}
                        onClick={() => {
                          setActiveNamespace(group.namespace);
                          setSearch("");
                        }}
                      >
                        <span className="role-modal__tab-label">{group.label}</span>
                        <span className="role-modal__tab-count">{selectedInGroup}/{group.permissions.length}</span>
                      </button>
                    );
                  })}
                </div>

                {activeGroupUnfiltered && (
                  <div className="role-modal__scope-panel">
                    <div className="role-modal__scope-panel-header">
                      <span className="role-modal__scope-panel-title">{activeGroupUnfiltered.label} permissions</span>
                      <label className="role-modal__scope-search">
                        <SearchOutlined sx={{ fontSize: 16, color: "#9ca3af", flexShrink: 0 }} />
                        <input
                          type="text"
                          placeholder={`Search permissions inside ${activeGroupUnfiltered.label}…`}
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          aria-label={`Search permissions inside ${activeGroupUnfiltered.label}`}
                        />
                      </label>
                      {!hasParents && (
                        <button
                          type="button"
                          className="role-modal__select-all"
                          onClick={() => toggleGroup(activeGroup, !activeGroupAllSelected)}
                        >
                          {activeGroupAllSelected ? "Clear all" : "Select all"}
                        </button>
                      )}
                    </div>
                    <div className="role-modal__scope-panel-body">
                      {activeGroup.permissions.length === 0 ? (
                        <p style={{ fontSize: 13, color: "#9ca3af" }}>No permissions match &quot;{search}&quot;.</p>
                      ) : (
                        <>
                          {activeGroupTree.parents.map((node) => renderTreeNode(node, activeGroup.namespace))}
                          {activeGroupTree.leaves.map((permission) => renderCheckbox(permission, activeGroup.namespace))}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="role-modal__footer">
          <button type="button" className="role-modal__btn role-modal__btn--cancel" onClick={onClose} disabled={isLoading}>
            Cancel
          </button>
          <button type="button" className="role-modal__btn role-modal__btn--save" onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? <CircularProgress size={14} sx={{ color: "#fff", mr: 1 }} /> : <SaveOutlined style={{ fontSize: 16, marginRight: 6 }} />}
            {isLoading ? "Creating…" : "Create role"}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

CreateRoleDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  knownPermissionCatalog: PropTypes.arrayOf(PropTypes.string).isRequired,
  onCreated: PropTypes.func.isRequired,
};

export default CreateRoleDrawer;
